import { useEffect, useState } from 'react'
import BigNumber from 'bignumber.js'
import { useWeb3React } from '@web3-react/core'
import store from 'state'
import { getTokenPricesFromFarm } from 'state/pools/helpers'
import multicall from 'utils/multicall'
import { getCraftsmanV2Address } from 'utils/addressHelpers'
import { BIG_ZERO } from 'utils/bigNumber'
import { getBalanceAmount } from 'utils/formatBalance'
import { farmsConfig } from 'config/constants'
import { SerializedFarmConfig } from 'config/constants/types'
import craftsmanV2ABI from 'config/abi/craftsmanV2.json'
import useRefresh from 'hooks/useRefresh'
import { serializeTokensByAddress } from 'config/constants/tokens'
import { TOKEN_PRICE_MAPPING } from 'config/constants/tokenPriceMappings'
import isEqual from 'lodash/isEqual'

const tokenList = serializeTokensByAddress()
export interface FarmWithBalance extends SerializedFarmConfig {
  earningsV2: any[]
}

const useFarmsV2WithBalance = () => {
  const [farmsV2WithStakedBalance, setFarmsV2WithStakedBalance] = useState<FarmWithBalance[]>([])
  const [earningsUsdc, setEarningsUsdc] = useState<number>(null)
  const { account } = useWeb3React()
  const { slowRefresh } = useRefresh()

  const [prices, setPrices] = useState(null)
  const farms = store.getState().farms.data
  useEffect(() => {
    const rewardPrices = getTokenPricesFromFarm(store.getState().farms.data)
    if (!isEqual(rewardPrices, prices)) {
      setPrices(rewardPrices)
    }
  }, [farms, prices])

  useEffect(() => {
    const fetchBalances = async () => {
      const farmsV2 = farmsConfig.filter((farmConfig) => farmConfig.version === 2)
      const calls = farmsV2.map((farm) => ({
        address: getCraftsmanV2Address(),
        name: 'pendingTokens',
        params: [farm.pid, account],
      }))

      const rawEarnings = await multicall(craftsmanV2ABI, calls)
      const parsedEarnings = rawEarnings.map(([tokenAddrs, earnings]) => {
        return { tokens: tokenAddrs, earnings: earnings.map((earning) => new BigNumber(earning._hex)) }
      })
      const results = farmsV2.map((farm, index) => ({ ...farm, earningsV2: parsedEarnings[index] }))
      let totalEarned = BIG_ZERO
      const farmsWithEarnings = results.filter((farm) => {
        let amountGT0 = false
        farm.earningsV2.earnings.forEach((amount: BigNumber, index: number) => {
          if (amount.gt(0)) {
            amountGT0 = true
            const tokenAddr = farm.earningsV2.tokens[index]
            const mappedTokenAddr = TOKEN_PRICE_MAPPING[tokenAddr.toLowerCase()] ?? tokenAddr
            totalEarned = totalEarned.plus(
              getBalanceAmount(amount, tokenList[mappedTokenAddr]?.decimals).times(
                prices[mappedTokenAddr.toLowerCase()],
              ),
            )
          }
        })
        return amountGT0
      })

      setFarmsV2WithStakedBalance(farmsWithEarnings)
      setEarningsUsdc(totalEarned.toNumber())
    }

    if (account && prices) {
      fetchBalances()
    }
  }, [account, prices, slowRefresh])

  return { farmsV2WithStakedBalance, earningsUsdc }
}

export default useFarmsV2WithBalance
